
import { supabase } from "@/integrations/supabase/client";
import { Interview } from "@/types/interview";
import { InterviewMapperService } from "./base/interviewMapperService";
import { toast } from "sonner";

export class InterviewUpdateService {
  static async updateInterview(interviewId: string, updateData: Partial<Interview>): Promise<boolean> {
    console.log(`[InterviewUpdateService] Starting update for interview: ${interviewId}`);
    console.log('[InterviewUpdateService] === INCOMING updateData ANALYSIS ===');
    console.log('[InterviewUpdateService] Raw timestamp received:', {
      timestamp: updateData.timestamp,
      timestampType: typeof updateData.timestamp,
      hasTimestamp: 'timestamp' in updateData,
      timestampLength: updateData.timestamp?.length
    });
    console.log('[InterviewUpdateService] Update data overview:', { 
      title: updateData.title?.length || 0,
      intervieweeName: updateData.intervieweeName,
      company: updateData.company,
      contextLength: updateData.context?.length || 0,
      questionLength: updateData.question?.length || 0,
      answerLength: updateData.answer?.length || 0,
      photoUrl: updateData.photoUrl || 'No photo URL',
      contextUrl: updateData.contextUrl || 'No context URL',
      timestamp: updateData.timestamp,
      tagsCount: Array.isArray(updateData.tags) ? updateData.tags.length : 'n/a',
      tagsIsArray: Array.isArray(updateData.tags)
    });

    try {
      // Convert client-side model to database model
      const dbUpdateData = this.mapClientModelToDatabaseModel(updateData);
      
      console.log('[InterviewUpdateService] Converted DB update data overview:', { 
        title: dbUpdateData.title?.length || 0,
        interviewee_name: dbUpdateData.interviewee_name,
        company: dbUpdateData.company,
        contextLength: dbUpdateData.context?.length || 0,
        questionLength: dbUpdateData.question?.length || 0,
        answerLength: dbUpdateData.answer?.length || 0,
        photo_url: dbUpdateData.photo_url || 'No photo URL',
        context_url: dbUpdateData.context_url || 'No context URL',
        tagsCount: Array.isArray(dbUpdateData.tags) ? dbUpdateData.tags.length : 'n/a',
        tagsIsArray: Array.isArray(dbUpdateData.tags)
      });

      // Add additional logging for photoUrl specifically
      if ('photo_url' in dbUpdateData) {
        console.log('[InterviewUpdateService] Photo URL being saved:', dbUpdateData.photo_url || 'empty string');
      } else {
        console.log('[InterviewUpdateService] No photo_url in update data');
      }
      
      // Log contextUrl if it exists
      if ('context_url' in dbUpdateData) {
        console.log('[InterviewUpdateService] Context URL being saved:', dbUpdateData.context_url || 'empty string');
      } else {
        console.log('[InterviewUpdateService] No context_url in update data');
      }
      
    // CRITICAL FIX: Special handling for tags to prevent PostgreSQL error
    if ('tags' in dbUpdateData) {
      // Make absolutely sure it's a valid array
      if (!Array.isArray(dbUpdateData.tags)) {
        dbUpdateData.tags = [];
      } else {
        // Clean and filter tags properly with enhanced cleaning
        dbUpdateData.tags = dbUpdateData.tags
          .filter(tag => tag !== null && tag !== undefined && typeof tag === 'string')
          .map(tag => {
            // Clean each tag thoroughly - remove ALL formatting artifacts
            let cleanTag = String(tag)
              .replace(/^["']|["']$/g, '') // Remove surrounding quotes
              .replace(/["']/g, '') // Remove ANY quotes within the string
              .replace(/^#+/, '') // Remove any leading hashtags
              .replace(/[{}]/g, '') // Remove curly braces
              .replace(/\s+/g, ' ') // Replace multiple spaces with single space
              .replace(/[^\w\sæøåÆØÅ-]/g, '') // Remove special characters except Norwegian letters and hyphens
              .trim();
            return cleanTag;
          })
          .filter(tag => tag.length > 0 && tag !== '{}' && tag !== '"#{}"'); // Only keep valid non-empty tags
      }
      
      console.log('[InterviewUpdateService] Processed tags for saving:', dbUpdateData.tags);
    } else {
      // Ensure tags is always present as empty array
      dbUpdateData.tags = [];
      console.log('[InterviewUpdateService] Adding empty tags array');
    }
      
      // Log timestamp if it exists
      if ('timestamp' in dbUpdateData) {
        console.log('[InterviewUpdateService] Timestamp being saved:', dbUpdateData.timestamp);
      }

      // Use standard ISO timestamp without additional random data
      dbUpdateData.updated_at = new Date().toISOString();
      console.log('[InterviewUpdateService] Using standard ISO timestamp for updated_at:', dbUpdateData.updated_at);

      // Use our force_interview_update database function
      console.log('[InterviewUpdateService] Using force_interview_update database function');
      
      const { data: result, error } = await supabase.rpc(
        'force_interview_update',
        {
          interview_id: interviewId,
          update_data: dbUpdateData
        }
      );
      
      if (error) {
        console.error('[InterviewUpdateService] Database function error:', error);
        toast.error(`Kunne ikke oppdatere intervju: ${error.message}`);
        return false;
      }
      
      console.log('[InterviewUpdateService] Database function result:', result);
      
      if (result === true) {
        toast.success('Intervju oppdatert');
        return true;
      } else {
        toast.error('Ingen data ble oppdatert');
        return false;
      }
    } catch (error) {
      console.error('[InterviewUpdateService] Unexpected error:', error);
      toast.error('En uventet feil oppstod');
      return false;
    }
  }

  /**
   * Delete an interview by ID using Edge Function
   */
  static async deleteInterview(interviewId: string): Promise<boolean> {
    console.log(`[InterviewUpdateService] Executing critical deletion for interview: ${interviewId}`);
    
    try {
      console.log("[InterviewUpdateService] Using edge function to delete interview with ID:", interviewId);
      
      // Call the edge function instead of direct database operation
      const { data, error } = await supabase.functions.invoke("delete-interview", {
        body: {
          interviewId: interviewId
        }
      });

      if (error) {
        console.error("[InterviewUpdateService] Error calling delete-interview function:", error);
        toast.error(`Kunne ikke slette intervju: ${error.message}`);
        return false;
      }

      if (!data || !data.success) {
        const errorMessage = (data?.message) || "Ukjent feil under sletting";
        console.error("[InterviewUpdateService] Deletion failed:", errorMessage);
        toast.error(`Kunne ikke slette intervju: ${errorMessage}`);
        return false;
      }

      console.log("[InterviewUpdateService] Interview successfully deleted via edge function");
      toast.success('Intervju slettet');
      return true;
    } catch (error) {
      console.error("[InterviewUpdateService] Exception during critical deletion operation:", error);
      toast.error('En uventet feil oppstod ved sletting av intervju');
      return false;
    }
  }

  private static mapClientModelToDatabaseModel(interviewData: Partial<Interview>): Record<string, any> {
    const dbModel: Record<string, any> = {};
    
    // Explicit mapping of each field
    if (interviewData.title !== undefined) dbModel.title = interviewData.title;
    if (interviewData.jobTitle !== undefined) dbModel.job_title = interviewData.jobTitle;
    if (interviewData.intervieweeName !== undefined) dbModel.interviewee_name = interviewData.intervieweeName;
    if (interviewData.company !== undefined) dbModel.company = interviewData.company;
    if (interviewData.context !== undefined) dbModel.context = interviewData.context;
    if (interviewData.question !== undefined) dbModel.question = interviewData.question;
    if (interviewData.answer !== undefined) dbModel.answer = interviewData.answer;
    
    // CRITICAL FIX: Special handling for tags array to prevent PostgreSQL error
    if ('tags' in interviewData) {
      // Make absolutely sure it's a valid array
      if (Array.isArray(interviewData.tags)) {
        // Clean and filter tags properly with enhanced cleaning
        const validTags = interviewData.tags
          .filter(tag => tag !== null && tag !== undefined && typeof tag === 'string')
          .map(tag => {
            // Clean each tag thoroughly - remove ALL formatting artifacts  
            let cleanTag = String(tag)
              .replace(/^["']|["']$/g, '') // Remove surrounding quotes
              .replace(/["']/g, '') // Remove ANY quotes within the string
              .replace(/^#+/, '') // Remove any leading hashtags
              .replace(/[{}]/g, '') // Remove curly braces
              .replace(/\s+/g, ' ') // Replace multiple spaces with single space
              .replace(/[^\w\sæøåÆØÅ-]/g, '') // Remove special characters except Norwegian letters and hyphens
              .trim();
            return cleanTag;
          })
          .filter(tag => tag.length > 0 && tag !== '{}' && tag !== '"#{}"'); // Only keep valid non-empty tags
        
        console.log('[InterviewUpdateService] Processing tags - Input:', interviewData.tags, 'Valid tags:', validTags);
        console.log('[DEBUG] Saving to database with these tags:', validTags);
        
        // Store as JavaScript array - Supabase will handle PostgreSQL conversion
        dbModel.tags = validTags;
      } else {
        // If it's not an array or is null/undefined, use empty array
        console.log('[InterviewUpdateService] Tags is not an array, setting empty array');
        dbModel.tags = [];
      }
      
      console.log('[InterviewUpdateService] Final tags for DB:', dbModel.tags, 'Type:', typeof dbModel.tags, 'Is Array:', Array.isArray(dbModel.tags));
    } else {
      // Always include tags even if not in the input data
      console.log('[InterviewUpdateService] No tags field in input, setting empty array');
      dbModel.tags = [];
    }
    
    // Handle photoUrl with explicit null/undefined checking - skip empty values
    if ('photoUrl' in interviewData && interviewData.photoUrl && interviewData.photoUrl.trim() !== '') {
      console.log('[InterviewUpdateService] Setting photo_url in DB model:', interviewData.photoUrl);
      dbModel.photo_url = interviewData.photoUrl;
    } else if ('photoUrl' in interviewData && (interviewData.photoUrl === '' || interviewData.photoUrl === undefined || interviewData.photoUrl === null)) {
      console.log('[InterviewUpdateService] Skipping empty/null photoUrl to preserve existing value');
      // Don't include photo_url in update to preserve existing DB value
    }
    
    // Handle contextUrl with explicit null/undefined checking - skip empty values
    if ('contextUrl' in interviewData && interviewData.contextUrl && interviewData.contextUrl.trim() !== '') {
      console.log('[InterviewUpdateService] Setting context_url in DB model:', interviewData.contextUrl);
      dbModel.context_url = interviewData.contextUrl;
    } else if ('contextUrl' in interviewData && (interviewData.contextUrl === '' || interviewData.contextUrl === undefined || interviewData.contextUrl === null)) {
      console.log('[InterviewUpdateService] Skipping empty/null contextUrl to preserve existing value');
      // Don't include context_url in update to preserve existing DB value
    }
    
    // Handle contextImageUrl with explicit null/undefined checking
    if ('contextImageUrl' in interviewData) {
      console.log('[InterviewUpdateService] Setting context_image_url in DB model:', interviewData.contextImageUrl);
      dbModel.context_image_url = interviewData.contextImageUrl;
    }
    
    // Handle contextImageCaption with explicit null/undefined checking
    if ('contextImageCaption' in interviewData) {
      console.log('[InterviewUpdateService] Setting context_image_caption in DB model:', interviewData.contextImageCaption);
      dbModel.context_image_caption = interviewData.contextImageCaption;
    }
    
    // Handle timestamp field - preserve user's exact timestamp
    if (interviewData.timestamp !== undefined) {
      console.log('[InterviewUpdateService] Processing timestamp field:', {
        originalValue: interviewData.timestamp,
        type: typeof interviewData.timestamp
      });
      
      // Simply pass through the timestamp as-is if it's a valid ISO string
      if (typeof interviewData.timestamp === 'string' && interviewData.timestamp.length > 0) {
        try {
          // Only validate that it can be parsed as a date, but don't convert it
          const testDate = new Date(interviewData.timestamp);
          if (!isNaN(testDate.getTime())) {
            // Use the original string exactly as provided
            dbModel.timestamp = interviewData.timestamp;
            console.log('[InterviewUpdateService] Using exact provided timestamp in DB model:', dbModel.timestamp);
          } else {
            console.log('[InterviewUpdateService] Invalid timestamp provided, skipping');
          }
        } catch (e) {
          console.log('[InterviewUpdateService] Error parsing timestamp, skipping:', e);
        }
      } else {
        console.log('[InterviewUpdateService] Empty or invalid timestamp type, skipping');
      }
    }
    
    // Always include updated_at as a clean ISO timestamp
    dbModel.updated_at = new Date().toISOString();
    console.log('[InterviewUpdateService] Setting updated_at in DB model:', dbModel.updated_at);

    return dbModel;
  }
}
